// src/components/themes/default/admin/BranchEmployeesTab.tsx
"use client";

import { useState, useEffect, useCallback } from "react";
import {
  Users,
  ShieldCheck,
  Building2,
  Phone,
  Mail,
  Calendar,
  RefreshCw,
  Loader2,
  Crown,
  Briefcase,
  CheckCircle2,
  Lock,
} from "lucide-react";

interface BranchEmployeesTabProps {
  branchInfo: any;
  subdomain: string;
  theme?: "dark" | "light";
  primaryColor?: string;
  onNavigateToAttendance?: () => void;
}

export default function BranchEmployeesTab({
  branchInfo,
  subdomain,
  theme = "dark",
  primaryColor = "#059669",
  onNavigateToAttendance,
}: BranchEmployeesTabProps) {
  const isLight = theme === "light";
  const branchId = branchInfo?.id;
  const branchName = branchInfo?.name || "Branch Outlet";

  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const getBranchToken = useCallback(() => {
    if (typeof window === "undefined") return "";
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("branch_auth_")) {
          const data = JSON.parse(localStorage.getItem(key) || "{}");
          if (data?.token) return data.token;
        }
      }
    } catch {}
    return localStorage.getItem("token") || "";
  }, []);

  const getApiUrl = useCallback(
    (endpoint: string) => {
      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname.includes("localhost") ||
          window.location.hostname === "127.0.0.1");
      return isLocal ? `http://${subdomain}.localhost:8000${endpoint}` : endpoint;
    },
    [subdomain]
  );

  const fetchEmployees = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const token = getBranchToken();
      const res = await fetch(
        getApiUrl(`/api/v1/hrm/employees/?branch_id=${branchId}`),
        {
          headers: {
            Accept: "application/json",
            "X-Branch-ID": String(branchId),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.results || [];
        setEmployees(list);
      }
    } catch (err) {
      console.error("Error fetching branch employees:", err);
    } finally {
      setLoading(false);
    }
  }, [branchId, getApiUrl, getBranchToken]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  return (
    <div className="space-y-6 font-sans animate-in fade-in pb-12">
      {/* 🌟 Top Header Toolbar */}
      <div
        className={`p-6 rounded-3xl border flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-all ${
          isLight
            ? "bg-white border-slate-200 shadow-sm text-slate-900"
            : "bg-slate-900 border-slate-800 text-white shadow-xl"
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            style={{
              backgroundColor: `${primaryColor}18`,
              color: primaryColor,
              borderColor: `${primaryColor}35`,
            }}
            className="w-12 h-12 rounded-2xl flex items-center justify-center border"
          >
            <Users className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-base sm:text-lg font-black tracking-tight">
                Branch Staff & Employees
              </h1>
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 font-mono">
                {employees.length} Team Members
              </span>
            </div>
            <p className={`text-xs ${isLight ? "text-slate-500" : "text-slate-400"}`}>
              {branchName} • সকল ডিপার্টমেন্ট ও পজিশন মেইন মার্চেন্ট সদর দপ্তর থেকে নির্ধারিত
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          {onNavigateToAttendance && (
            <button
              onClick={onNavigateToAttendance}
              style={{ backgroundColor: primaryColor }}
              className="px-4 py-2.5 rounded-2xl text-xs font-bold text-white shadow-lg hover:opacity-90 active:scale-95 cursor-pointer transition-all flex items-center gap-2"
            >
              <Calendar className="w-4 h-4" />
              <span>Mark Attendance</span>
            </button>
          )}

          <button
            onClick={fetchEmployees}
            disabled={loading}
            className={`p-2.5 rounded-2xl border transition-all cursor-pointer ${
              isLight
                ? "bg-slate-50 border-slate-200 hover:bg-slate-100 text-slate-700"
                : "bg-slate-950 border-slate-800 hover:bg-slate-800 text-slate-300"
            }`}
            title="Refresh List"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* 🌟 পলিসি ব্যানার */}
      <div
        className={`p-4 rounded-2xl border flex items-center justify-between text-xs font-medium ${
          isLight
            ? "bg-blue-50/70 border-blue-200 text-blue-900"
            : "bg-blue-950/20 border-blue-800/40 text-blue-300"
        }`}
      >
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-blue-500 shrink-0" />
          <span>
            <strong>সেন্ট্রাল মার্চেন্ট পলিসি:</strong> ডিপার্টমেন্ট, পদবি এবং বেতন মেইন মার্চেন্ট পরিচালনা করে। ব্রাঞ্চে সুপার ম্যানেজারসহ সকল কর্মীর দৈনিক উপস্থিতি গ্রহণ প্রযোজ্য।
          </span>
        </div>
      </div>

      {loading ? (
        <div className="py-24 text-center">
          <Loader2 style={{ color: primaryColor }} className="w-8 h-8 animate-spin mx-auto mb-2" />
          <p className="text-xs font-bold text-slate-400">Loading branch team members...</p>
        </div>
      ) : employees.length === 0 ? (
        <div
          className={`p-12 text-center rounded-3xl border-2 border-dashed space-y-3 ${
            isLight ? "bg-white border-slate-200 text-slate-500" : "bg-slate-900/60 border-slate-800 text-slate-400"
          }`}
        >
          <Users className="w-10 h-10 mx-auto text-slate-400 opacity-60" />
          <h4 className="text-sm font-black">কোনো কর্মী পাওয়া যায়নি</h4>
          <p className="text-xs max-w-sm mx-auto">
            মেইন মার্চেন্ট অ্যাডমিন থেকে এই ব্রাঞ্চের ম্যানেজার অথবা নতুন কর্মী অ্যাসাইন করুন।
          </p>
        </div>
      ) : (
        <div
          className={`p-6 rounded-3xl border space-y-4 ${
            isLight ? "bg-white border-slate-200 shadow-xs" : "bg-slate-900 border-slate-800 text-white"
          }`}
        >
          <div className="flex items-center justify-between border-b pb-4 border-slate-100 dark:border-slate-800">
            <h3 className="text-sm font-black flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-emerald-500" />
              <span>Assigned Branch Team Members ({employees.length})</span>
            </h3>
            <span className="text-xs text-slate-400 font-mono">
              Controlled by Main Merchant
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {employees.map((emp) => {
              const isMgr = Boolean(emp.is_manager);
              return (
                <div
                  key={emp.id}
                  className={`p-5 rounded-3xl border-2 transition-all space-y-3 relative overflow-hidden ${
                    isMgr
                      ? isLight
                        ? "bg-gradient-to-br from-emerald-50/80 via-white to-emerald-50/30 border-emerald-400 shadow-md ring-2 ring-emerald-500/10"
                        : "bg-gradient-to-br from-emerald-950/30 via-slate-900 to-slate-900 border-emerald-500/50 shadow-xl"
                      : isLight
                      ? "bg-slate-50/80 border-slate-200 hover:bg-white"
                      : "bg-slate-950 border-slate-800"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`w-10 h-10 rounded-2xl flex items-center justify-center font-black ${
                          isMgr
                            ? "bg-emerald-500 text-white shadow-md shadow-emerald-500/30"
                            : "bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                        }`}
                      >
                        {isMgr ? <Crown className="w-5 h-5" /> : emp.full_name?.charAt(0) || "E"}
                      </div>
                      <div>
                        <h4 className="text-sm font-black text-slate-900 dark:text-white leading-tight">
                          {emp.full_name}
                        </h4>
                        <span className="text-[10px] font-mono text-slate-400">
                          ID: #{emp.employee_id}
                        </span>
                      </div>
                    </div>

                    {isMgr ? (
                      <span className="px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider bg-emerald-500 text-white font-mono flex items-center gap-1 shadow-xs">
                        <Crown className="w-2.5 h-2.5" /> Super Manager
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                        {emp.status}
                      </span>
                    )}
                  </div>

                  <div className="space-y-1.5 text-xs pt-1 border-t border-slate-200/60 dark:border-slate-800/80">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Department:</span>
                      <span className="font-bold text-slate-800 dark:text-slate-200">
                        {emp.department_name || "Store Operations"}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-slate-400">Designation / Role:</span>
                      <span className={`font-bold ${isMgr ? "text-emerald-600 dark:text-emerald-400" : "text-slate-700 dark:text-slate-300"}`}>
                        {emp.designation_title || (isMgr ? "Branch Super Manager" : "Staff")}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-slate-400">Phone:</span>
                      <span className="font-mono text-slate-800 dark:text-slate-200">
                        {emp.phone || "N/A"}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-slate-400">Joined:</span>
                      <span className="font-mono text-slate-500 text-[11px]">
                        {emp.joining_date || "Active"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}